import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {handleErrorResponse} from "../utils/util"
import { USER_ROLE } from "../constants/index"
import { ControlService } from "../api/services.gen";
import {
    ControlApiSuperUserListData,
    ControlApiSuperUserCreateData,
    ControlApiSuperUserRetrieveData,
    ControlApiSuperUserUpdateData,
    ControlApiSuperUserDestroyData,
    ControlApiSuperUserChangeAdminCompanyCreateData,
    ControlApiSuperUserUpdateAvatarUpdateData,
} from "../api/types.gen";
import {INTERNAL_SERVER_ERROR} from "../constants/index";
import i18n from 'i18next';
import { resetAppState } from "../store/common";

export const getSuperUserList = createAsyncThunk(
    "getSuperUserList",
    async (data: ControlApiSuperUserListData, thunkAPI: any) => {
        try {
            const res: any = await ControlService.controlApiSuperUserList(data);
            if (res.results.success) {
                return res;
            } else {
                if(res.message) return thunkAPI.rejectWithValue(res.message);
                else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
            }
        } catch (error: any) {
            return handleErrorResponse(error, USER_ROLE.CONTROL, thunkAPI);
        }
    },
);

export const createSuperUser = createAsyncThunk(
    "createSuperUser",
    async (data: ControlApiSuperUserCreateData, thunkAPI: any) => {
        try {
            const res: any = await ControlService.controlApiSuperUserCreate(data);
            if (res.success) {
                return res;
            } else {
                if(res.message) return thunkAPI.rejectWithValue(res.message);
                else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
            }
        } catch (error: any) {
            return handleErrorResponse(error, USER_ROLE.CONTROL, thunkAPI);
        }
    },
);

export const getSuperUserDetail = createAsyncThunk(
    "getSuperUserDetail",
    async (data: ControlApiSuperUserRetrieveData, thunkAPI: any) => {
        try {
            const res: any = await ControlService.controlApiSuperUserRetrieve(data);
            if (res.id) {
                return res;
            } else {
                if(res.message) return thunkAPI.rejectWithValue(res.message);
                else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
            }
        } catch (error: any) {
            return handleErrorResponse(error, USER_ROLE.CONTROL, thunkAPI);
        }
    },
);

export const updateSuperUserDetail = createAsyncThunk(
    "updateSuperUserDetail",
    async (data: ControlApiSuperUserUpdateData, thunkAPI: any) => {
        try {
            const res: any = await ControlService.controlApiSuperUserUpdate(data);
            if (res.success) {
                return res;
            } else {
                if(res.message) return thunkAPI.rejectWithValue(res.message);
                else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
            }
        } catch (error: any) {
            return handleErrorResponse(error, USER_ROLE.CONTROL, thunkAPI);
        }
    },
);

export const deleteSuperUserDetail = createAsyncThunk(
    "deleteSuperUserDetail",
    async (data: ControlApiSuperUserDestroyData, thunkAPI: any) => {
        try {
            const res: any = await ControlService.controlApiSuperUserDestroy(data);
            if (res.success) {
                return res;
            } else {
                if(res.message) return thunkAPI.rejectWithValue(res.message);
                else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
            }
        } catch (error: any) {
            return handleErrorResponse(error, USER_ROLE.CONTROL, thunkAPI);
        }
    },
);

export const getCompanyOptions = createAsyncThunk(
    "getCompanyOptions",
    async (_, thunkAPI: any) => {
        try {
            const res: any = await ControlService.controlApiSuperUserGetOptionsRelatedRetrieve();
            if (res.success) {
                return res;
            } else {
                if(res.message) return thunkAPI.rejectWithValue(res.message);
                else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
            }
        } catch (error: any) {
            return handleErrorResponse(error, USER_ROLE.CONTROL, thunkAPI);
        }
    },
);

export const changeStaffCompany = createAsyncThunk(
    "changeStaffCompany",
    async (data: ControlApiSuperUserChangeAdminCompanyCreateData, thunkAPI: any) => {
        try {
            const res: any = await ControlService.controlApiSuperUserChangeAdminCompanyCreate(data);
            if (res.success) {
                return res;
            } else {
                if(res.message) return thunkAPI.rejectWithValue(res.message);
                else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
            }
        } catch (error: any) {
            return handleErrorResponse(error, USER_ROLE.CONTROL, thunkAPI);
        }
    },
);

export const updateSuperUserAvatar = createAsyncThunk(
    "updateSuperUserAvatar",
    async (data: ControlApiSuperUserUpdateAvatarUpdateData, thunkAPI: any) => {
        try {
            const res: any = await ControlService.controlApiSuperUserUpdateAvatarUpdate(data);
            if (res.success) {
                return res;
            } else {
                if(res.message) return thunkAPI.rejectWithValue(res.message);
                else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
            }
        } catch (error: any) {
            return handleErrorResponse(error, USER_ROLE.CONTROL, thunkAPI);
        }
    },
);


export interface TableHeader {
    title: string;
    isSort: boolean;
    field: string;
    currentSortState: number;
    customStyle: string;
}

const initTableHeaders = [
    {
        title: "ID",
        isSort: true,
        field: "id",
        currentSortState: 0,
        customStyle: "w-[80px]",
    },
    {
        title: "名前",
        isSort: true,
        field: "fullname",
        currentSortState: 0,
        customStyle: "w-[180px]",
    },
    {
        title: "メールアドレス",
        isSort: true,
        field: "user__email",
        currentSortState: 0,
        customStyle: "w-[320px]",
    },
    {
        title: "登録日",
        isSort: true,
        field: "created_at",
        currentSortState: 1,
        customStyle: "",
    },
]

// Initial state of Redux
interface SuperUserState {
    loading: boolean;
    error: string | null;
    success: string | null;
    actionName: string | null;
    actionStatus: string;
    userList: [];
    countItems: number;
    currentPage: number;
    pageSize: number;
    sortBy: string | null;
    sortOrder: string | null;
    searchQuery: string | null;
    totalResults: number;
    tableHeaders: TableHeader[];
    detailData: [];
    companyOptions: {};
    superUserLogged: [];
    isSearch: boolean;
}

const initialState: SuperUserState = {
    loading: false,
    error: null,
    success: null,
    actionName: null,
    actionStatus: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
    userList: [],
    countItems: 0,
    currentPage: 1,
    pageSize: 10,
    sortBy: "created_at",
    sortOrder: "desc",
    searchQuery: "",
    totalResults: 0, 
    tableHeaders: initTableHeaders,
    detailData: [],
    companyOptions: {},
    superUserLogged: [],
    isSearch: false,
};

const sortingStates = ["default", "desc", "asc"];

export const superUserSlice = createSlice({
    name: "superUser",
    initialState,
    reducers: {
        setSuccess: (state, action) => {
            state.success = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        setCurrentPage: (state, action) => {
            state.currentPage = action.payload;
        },
        setSearchQuery: (state, action) => {
            state.searchQuery = action.payload;
        },
        handleSortByHeader(state, action) {
            const sortHeader = action.payload;
            state.tableHeaders = state.tableHeaders.map((header, idx) => (header.field === sortHeader.field)
            ? ({...header, currentSortState: (header.currentSortState + 1) % sortingStates.length})
            : ({...header, currentSortState: 0}));
            
            if((sortHeader.currentSortState + 1) % sortingStates.length !== 0) {
                state.sortBy = sortHeader.field;
                state.sortOrder = sortingStates[(sortHeader.currentSortState + 1) % sortingStates.length];
            } else {
                state.sortBy = null;
                state.sortOrder = null;
            }
        },
        setSuperUserLogged: (state, action) => {
            state.superUserLogged = action.payload;
        },
        setIsSearch: (state, action) => {
            state.isSearch = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
        // reset state
        .addCase(resetAppState, () => initialState)

        // getSuperUserList
        .addCase(getSuperUserList.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(getSuperUserList.fulfilled, (state, action) => {
            state.loading = false;
            state.userList = action.payload.results?.data;
            state.totalResults = action.payload.results?.total_records;
            state.countItems = action.payload.count;
        })
        .addCase(getSuperUserList.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        })

        // createSuperUser
        .addCase(createSuperUser.pending, (state) => {
            state.loading = true;
            state.error = null;
            state.success = null;
            state.actionName = "createSuperUser";
        })
        .addCase(createSuperUser.fulfilled, (state, action) => {
            state.loading = false;
            state.success = action.payload.message;
            state.detailData = action.payload.data;
        })
        .addCase(createSuperUser.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        })

        // getSuperUserDetail
        .addCase(getSuperUserDetail.pending, (state) => {
            state.loading = true;
            state.error = null;
            state.success = null;
            state.actionName = "getSuperUserDetail";
            state.actionStatus = "pending";
        })
        .addCase(getSuperUserDetail.fulfilled, (state, action) => {
            state.loading = false;
            state.detailData = action.payload;
            state.actionStatus = "fulfilled";
        })
        .addCase(getSuperUserDetail.rejected, (state, action) => {
            state.loading = false;
            // state.error = action.payload as string;
            state.actionStatus = "rejected";
        })

        // updateSuperUserDetail
        .addCase(updateSuperUserDetail.pending, (state) => {
            state.loading = true;
            state.error = null;
            state.success = null;
            state.actionName = "updateSuperUserDetail";
        })
        .addCase(updateSuperUserDetail.fulfilled, (state, action) => {
            state.loading = false;
            state.success = action.payload.message;
            state.detailData = action.payload.data;
        })
        .addCase(updateSuperUserDetail.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        })

        // deleteSuperUserDetail
        .addCase(deleteSuperUserDetail.pending, (state) => {
            state.loading = true;
            state.error = null;
            state.success = null;
            state.actionName = "deleteSuperUserDetail";
        })
        .addCase(deleteSuperUserDetail.fulfilled, (state, action) => {
            state.loading = false;
            state.success = action.payload.message;
        })
        .addCase(deleteSuperUserDetail.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        })

        // getCompanyOptions
        .addCase(getCompanyOptions.pending, (state) => {
            state.loading = true;
            state.error = null;
            state.success = null;
        })
        .addCase(getCompanyOptions.fulfilled, (state, action) => {
            state.loading = false;
            state.companyOptions = action.payload.data.companys;
        })
        .addCase(getCompanyOptions.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        })

        // changeStaffCompany
        .addCase(changeStaffCompany.pending, (state) => {
            state.loading = true;
            state.error = null;
            state.success = null;
            state.actionName = "changeStaffCompany";
        })
        .addCase(changeStaffCompany.fulfilled, (state, action) => {
            state.loading = false;
            state.success = action.payload.message;
        })
        .addCase(changeStaffCompany.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        })

        // updateSuperUserAvatar
        .addCase(updateSuperUserAvatar.pending, (state) => {
            state.loading = true;
            state.error = null;
            state.success = null;
            state.actionName = "updateSuperUserAvatar";
        })
        .addCase(updateSuperUserAvatar.fulfilled, (state, action) => {
            state.loading = false;
            state.success = action.payload.message;
            state.superUserLogged = action.payload.data;
        })
        .addCase(updateSuperUserAvatar.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        })
    }
});
export const {
    setSuccess,
    setError,
    setCurrentPage,
    setSearchQuery,
    handleSortByHeader,
    setSuperUserLogged,
    setIsSearch,
} = superUserSlice.actions;
export default superUserSlice.reducer;













